<script setup lang="ts">
import { computed, defineAsyncComponent, onUnmounted, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { Toaster } from '@/components/ui/sonner';
import { MAX_VISIBLE_TOASTS } from '@/stores/toast';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppTooltip from '@/components/layout/AppTooltip.vue';
import SheetTabs from '@/components/sheet/SheetTabs.vue';
import { useThemeStore } from '@/stores/theme';
import { useAppearanceStore } from '@/stores/appearance';
import { useAuthStore } from '@/stores/auth';
import { useTableFullscreen } from '@/composables/useTableFullscreen';
import { useRealtime } from '@/composables/useRealtime';
import { initializeObservation } from '@/composables/useObservation';
import ObservationBanner from '@/components/layout/ObservationBanner.vue';
import ModuleRecoveryDialog from '@/components/layout/ModuleRecoveryDialog.vue';
import { useMascotStore } from '@/stores/mascot';

const CatMascot = defineAsyncComponent(() => import('@/components/mascot/CatMascot.vue'));

/**
 * Каркас приложения: шапка сверху, содержимое под ней.
 *
 * Шапка живёт здесь, а не внутри страниц: при переходе между разделами она
 * не перерисовывается — не мигает марка, не подпрыгивает содержимое, не
 * теряется меню профиля. Экраны «прихожей» (вход, смена пароля) шапки
 * не получают: там ещё нечего показывать, кроме формы.
 */
const route = useRoute();
const theme = useThemeStore();

/*
  Общий вид таблицы подтягивается после входа, а не при загрузке модуля:
  на экране входа запрос всё равно вернул бы «не авторизован». До ответа
  линии уже нарисованы по кэшу — его применяет inline-скрипт в index.html.
*/
const auth = useAuthStore();
const mascot = useMascotStore();
const appearance = useAppearanceStore();
const realtime = useRealtime();
initializeObservation(useRouter());

const SECTION_LABELS: Record<string, string> = {
  grid: 'Таблица 2026', candidate: 'Карточка кандидата', sheet: 'Лист',
  dashboard: 'Отчёты', blacklist: 'Чёрный список', users: 'Сотрудники',
  journal: 'Журнал', activity: 'История действий', settings: 'Настройки', import: 'Импорт и выгрузка', mailing: 'Рассылка',
};
watch(() => [auth.user?.id, auth.user?.role, route.path] as const, ([userId, role], previous) => {
  if (!userId) {
    realtime.stop(true);
    return;
  }
  if (previous && (previous[0] !== userId || previous[1] !== role)) realtime.stop(true);
  realtime.setActivityContext({
    path: route.path,
    label: SECTION_LABELS[String(route.name)] ?? 'Приложение',
    ...(route.name === 'sheet' ? { sheetCode: String(route.params.code ?? '') } : {}),
  });
  realtime.start();
}, { immediate: true, flush: 'sync' });
onUnmounted(() => realtime.stop(true));

watch(
  () => auth.isAuthenticated,
  (yes) => {
    if (yes) void appearance.ensureLoaded();
    else appearance.reset();
  },
  { immediate: true },
);

const fullscreen = useTableFullscreen();

const BARE_ROUTES = new Set(['login', 'not-found']);
/*
  Шапка уходит и в полноэкранном просмотре таблицы: ради него он и нужен —
  две полосы над таблицей стоят двух-трёх строк кандидатов.
*/
const showHeader = computed(
  () => Boolean(route.name) && !BARE_ROUTES.has(String(route.name)) && !fullscreen.enabled.value,
);

/*
  Ярлыки листов — только там, где человек работает с таблицей: на самой
  таблице и на листах. На «Отчётах» или «Сотрудниках» полоса вкладок
  внизу означала бы, что там тоже лист, а его там нет.

  В полноэкранном просмотре полосы нет по той же причине, что и шапки:
  ради него он и нужен.
*/
const TABLE_ROUTES = new Set(['grid', 'candidate', 'sheet']);
const showTabs = computed(
  () =>
    auth.isAuthenticated &&
    TABLE_ROUTES.has(String(route.name)) &&
    !fullscreen.enabled.value,
);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-app-bg">
    <AppHeader v-if="showHeader" />
    <ObservationBanner />

    <!--
      Прокрутка живёт здесь, а не на странице.

      `overflow-y-auto`, а не `hidden`: обычные страницы — журнал, чёрный
      список, настройки — длиннее экрана, и закрытое переполнение просто
      обрезало их снизу. Так и вышло: ради того, чтобы таблица не
      растягивала страницу и полоса ярлыков не уезжала под её последние
      строки, здесь стоял `overflow-hidden` — и прокрутка пропала везде,
      кроме самих таблиц.

      Таблицы при этом ничего не растягивают: и грид, и лист стоят на
      `h-full` и прокручиваются внутри себя, то есть ровно вписываются
      в эту область и лишней высоты не создают.
    -->
    <main class="min-h-0 flex-1 overflow-y-auto">
      <!--
        Переход между разделами — короткое проявление, без сдвига.

        Сдвиг («уехало влево, приехало справа») на каждом переходе читается
        как перезагрузка страницы; 120 мс по прозрачности достаточно, чтобы
        смена не выглядела рывком, и мало, чтобы не мешать работе.
      -->
      <RouterView v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" :key="route.name === 'candidate' ? 'grid' : route.name" />
        </Transition>
      </RouterView>
    </main>

    <SheetTabs v-if="showTabs" />
  </div>

  <!-- Тема передаётся явно: vue-sonner рисует свой слой и про класс .dark
       на корне ничего не знает — уведомления оставались светлыми -->
  <!-- Свои подсказки вместо нативных: нативная появляется через секунду
       с лишним, выглядит по-разному в каждой системе и не знает про тему -->
  <AppTooltip />
  <ModuleRecoveryDialog />
  <CatMascot v-if="mascot.visible && showHeader" :key="auth.user?.id" />

  <Toaster
    :theme="theme.isDark ? 'dark' : 'light'"
    position="bottom-right"
    :visible-toasts="MAX_VISIBLE_TOASTS"
    rich-colors
    close-button
  />
</template>

<style>
.page-enter-active,
.page-leave-active {
  transition: opacity 120ms ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* Человеку, которому анимации мешают, они не показываются вовсе */
@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
}
</style>
